import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordCaptureFields } from './types';

interface VSLeadsPasswordOverlayProps {
  config: PasswordCaptureFields;
  onSubmit?: (password: string) => void;
  isVisible: boolean;
}

const SHADOW_VALUES = [
  'none',
  '0 1px 2px rgba(0, 0, 0, 0.05)',
  '0 4px 6px rgba(0, 0, 0, 0.1)',
  '0 10px 15px rgba(0, 0, 0, 0.15)',
];

export function VSLeadsPasswordOverlay({
  config,
  onSubmit,
  isVisible,
}: VSLeadsPasswordOverlayProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (!isVisible) return null;

  const { layout, content, style } = config;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate password
    const isCorrect = content.password.case_sensitive
      ? password === content.password.value
      : password.toLowerCase() === content.password.value.toLowerCase();

    if (isCorrect) {
      setError('');
      onSubmit?.(password);
    } else {
      setError(content.password.error_message);
    }
  };

  const containerStyle: React.CSSProperties = {
    width: `${layout.width}px`,
    padding: `${layout.padding}px`,
    backgroundColor: style.container.bg_color,
    opacity: style.container.bg_opacity,
    borderRadius: `${style.container.border_radius}px`,
    boxShadow: SHADOW_VALUES[style.container.shadow] || 'none',
    border:
      style.container.border.width > 0
        ? `${style.container.border.width}px solid ${style.container.border.color}`
        : 'none',
    backdropFilter: style.container.glass_blur > 0 ? `blur(${style.container.glass_blur}px)` : 'none',
  };

  const positionClass = 
    layout.position === 'center'
      ? 'items-center justify-center'
      : layout.position === 'left'
      ? 'items-center justify-start pl-8'
      : 'items-center justify-end pr-8';

  const offsetStyle: React.CSSProperties = {
    paddingTop: `${layout.offset.top}px`,
    paddingRight: `${layout.offset.right}px`,
    paddingBottom: `${layout.offset.bottom}px`,
    paddingLeft: `${layout.offset.left}px`,
  };

  const overlayStyle: React.CSSProperties = {
    backgroundColor: `rgba(0, 0, 0, ${layout.overlay_opacity})`,
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: style.input.bg_color,
    color: style.input.text_color,
    borderColor: error ? '#EF4444' : style.input.border_color,
    borderRadius: `${style.input.border_radius}px`,
  };

  const titleOutside = content.title.position === 'outside';
  const showTitle = content.title.text;

  return (
    <>
      {/* Overlay escuro */}
      <div 
        className="absolute inset-0 z-40" 
        style={overlayStyle}
      />
      
      {/* Container do formulário */}
      <div 
        className={`absolute inset-0 z-50 flex ${positionClass}`} 
        style={offsetStyle}
      >
        <div className="flex flex-col items-center gap-4">
          {titleOutside && showTitle && (
            <h2
              className="text-2xl font-bold text-center"
              style={{ color: style.text.title_color }}
            >
              {content.title.text}
            </h2>
          )}

          <div style={containerStyle}>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!titleOutside && showTitle && (
                <h2
                  className="text-xl font-bold text-center mb-4"
                  style={{ color: style.text.title_color }}
                >
                  {content.title.text}
                </h2>
              )}

              <div>
                <Input
                  type="password"
                  placeholder={content.password.placeholder}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  className="w-full px-3 py-2 border"
                  style={inputStyle}
                />
                {error && (
                  <p className="text-xs text-red-500 mt-2">
                    {error}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full font-semibold transition-all"
                style={{
                  backgroundColor: style.button.bg_color,
                  color: style.button.text_color,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = style.button.hover_color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = style.button.bg_color;
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.backgroundColor = style.button.pressed_color;
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.backgroundColor = style.button.hover_color;
                }}
              >
                {content.button_label}
              </Button>

              {content.footer && (
                <p
                  className="text-xs text-center mt-3"
                  style={{ color: style.text.body_color }}
                >
                  {content.footer}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
